import styles from './StudyGuide.module.scss';
import { LessonStatus, Unit } from '../../../types';
import Button from '@src/components/button/Button';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { LoadingOutlined } from '@ant-design/icons';
import NormalCheckIcon from '../../../assets/icons/mark_check_normal.svg?react';
import MarkCheckIcon from '../../../assets/icons/mark_check.svg?react';
import MarkCrossIcon from 'src/assets/icons/cross_circle_filled.svg?react';
import IconOutline from 'src/assets/icons/new-outline-info.svg?react';
import IconArrowUp from 'src/assets/icons/dropdown-white.svg?react';
import { useGetUnitLazyQuery, useUpdateUnitStudyReviewStatusMutation, Content } from '@src/api/graphql-codegen';
import { useToast } from '@src/hooks';
import { Dropdown, MenuProps } from 'antd';
import Tooltip from 'antd/es/tooltip';
import { Collapse } from '@src/utils/componentCasts';
import MaterialCard from '@src/components/materialCard/Card';
import { logger } from '../../../utils/logger';

type Props = {
  unit: any;
  openPreview:() => void;
  startStudyGuide: () => void;
  handleDownloadMaterial?: (content: Content, lesson: any) => void;
};

function StudyGuide(props: Props) {
  const {
    unit,
    openPreview,
    startStudyGuide,
    handleDownloadMaterial
  } = props;

  const [isHovered, setIsHovered] = useState(false);
  const [complete, setComplete] = useState(false);
  const [completeRequire, setCompleteRequire] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [unitStudyHeading, setUnitStudyHeading] = useState('This Study Guide helps Teachers:');

  const [selectMaterial, setSelectMaterial] = useState(false);
  const { successToast } = useToast();

  const { studyGuideMaterials } = unit as Unit

  const [getUnitInfo, {loading: fetching}] = useGetUnitLazyQuery({ fetchPolicy: 'network-only'})
  const [updateUnitStudyReviewStatus, { loading }] = useUpdateUnitStudyReviewStatusMutation({
    onError(e) {
      logger.debug(`[e]`, e);
    },
    onCompleted() {
      getUnitInfo({
        variables: { input: { id: unit.id } },
      });
    },
  });

  useEffect(() => {
    const status = (unit as Unit).unitStudyReview?.status
    if (status === LessonStatus.Completed) {
      setComplete(true)
    } else {
      setComplete(false)
    }
    if ((unit as Unit).unitStudyHeading) {
      setUnitStudyHeading((unit as Unit).unitStudyHeading ?? 'This Study Guide helps Teachers:')
    }
  }, [unit])

  const skills: string[] = useMemo(() => {
    try {
      const box = (unit as Unit).unitStudyBox
      return JSON.parse(box || '[]')
    } catch {
      return []
    }
  }, [unit]);

  const markLessonAsComplete = useCallback(async (e) => {
    if (e.domEvent) {
      e.domEvent.stopPropagation()
    } else {
      e.stopPropagation()
    }
    if (unit?.id && !loading) {
      const res = await updateUnitStudyReviewStatus({
        variables: {
          input: {
            unitId: unit.id,
            status: complete ? LessonStatus.NotStarted : LessonStatus.Completed
          }
        }
      });
      if (res?.data?.updateUnitStudyReviewStatus) {
        successToast(`Successfully updated Lesson as ${complete ? 'Incomplete' : 'Complete'}.`);
      }
    }
  }, [complete, loading, unit, updateUnitStudyReviewStatus, successToast]);

  const cardStyle = useMemo(() => {
    const classes = [styles.card];

    if (completeRequire) {
      if (complete) {
        classes.push(styles.incompleteBorder);
        return classes.join(' ');
      } else {
        classes.push(styles.completeTopBorder);
        return classes.join(' ');
      }
    }

    if (isHovered) {
      classes.push(styles.hoverBorder);
      return classes.join(' ');
    }
    if (complete) {
      classes.push(styles.completeTopBorder);
    }

    return classes.join(' ');
  }, [completeRequire, complete, isHovered]);

  const completedCheckStyle = useMemo(() => {
    const classes = [styles.completedCheck];

    if (completeRequire) {
      if (complete) {
        classes.push(styles.normalBorder);
        return classes.join(' ');
      } else {
        classes.push(styles.completeTopBorder);
        return classes.join(' ');
      }
    }
    if (isHovered) {
      classes.push(styles.hoverBorder);
    }
    if (complete) {
      classes.push(styles.completeTopBorder);
    }
    return classes.join(' ');
  }, [completeRequire, complete, isHovered]);

  const completeCheckIconStyle = useMemo(() => {
    const classes = [styles.completeCheckIcon];
    if (completeRequire) {
      classes.push(styles.hidden);
    } else {
      if (complete) {
        classes.push(styles.shown);
      }
    }
    return classes.join(' ');
  }, [completeRequire, complete]);

  const inCompleteCheckIconStyle = useMemo(() => {
    const classes = [styles.inCompletedCrossIcon];

    if (complete) {
      classes.push(styles.hidden);
    }

    if (completeRequire) {
      classes.push(styles.shown);
    }
    return classes.join(' ');
  }, [completeRequire, complete]);

  const handleVisible = useCallback((visible: boolean) => {
    setShowMenu(visible);
  }, []);

  const handleOpenPreview  = () => {
    setShowMenu(false);
    openPreview()
  }
  const handleClickStart = (e: any) => {
    if (e.domEvent) {
      e.domEvent.stopPropagation()
    } else {
      e.stopPropagation()
    }
    openPreview()
  }
  const handleStartLesson  = () => {
    setShowMenu(false);
    startStudyGuide();
  }

  const handleSelectMaterials = useCallback((e: any) => {
    if (e.domEvent) {
      e.domEvent.stopPropagation()
    } else {
      e.stopPropagation()
    }
    setSelectMaterial(current => !current)
  }, [])

  const items: MenuProps['items'] = [
    {
      key: '1',
      label: (
        <div className={styles.dropDownMenuitem} onClick={handleOpenPreview}>
          Preview Unit Study Review
        </div>
      ),
    },
    {
      key: '2',
      label: (
        <div className={styles.dropDownMenuitem} onClick={handleStartLesson}>
          Start Unit Study Review
        </div>
      ),
    }]

  const materialViewStyle = useMemo(() => {
    const classes = [styles.materialSubView];

    if (!selectMaterial) {
      classes.push(styles.noborder);
    } else {
      if (completeRequire) {
        if (complete) {
          classes.push(styles.incompleteBorder);
          return classes.join(' ');
        } else {
          classes.push(styles.completeBorder);
          return classes.join(' ');
        }
      }
      if (complete) {
        classes.push(styles.completeBorder);
      }
      if (isHovered) {
        classes.push(styles.hoverBorder);
      }
    }
    return classes.join(' ');
  }, [completeRequire, complete, isHovered, selectMaterial]);

  const handleDownloadLessonMaterial = useCallback((content: Content) => {
    handleDownloadMaterial?.(content, undefined)
  }, [handleDownloadMaterial])

  const lessonMaterials = useMemo(() => {
    return (
      <div className={materialViewStyle}>
        <Collapse isOpened={selectMaterial}>
          <div className={styles.materialContents}>
            <div className={styles.unitsDiv}>
              {studyGuideMaterials?.map((material, index) => {
                return (
                  <MaterialCard content={material as Content} handleDownloadMaterial={handleDownloadLessonMaterial}/>
                )
              })}
            </div>
          </div>
        </Collapse>
      </div>
    )
  }, [studyGuideMaterials, selectMaterial, materialViewStyle, handleDownloadLessonMaterial])

  return (
    <div style={{position: 'relative'}}>
      <div className={styles.subTitle}>Unit Study Review</div>
      <Dropdown menu={{ items }} trigger={["contextMenu", "click"]} onOpenChange={handleVisible} open={showMenu} >
        <div className={cardStyle} onMouseOver={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} onClick={e => e.stopPropagation()}>
          <div className={styles.image} >
            <Button label='START' className={styles.startButton} onClick={handleClickStart}/>
          </div>
          <section className={styles.content}>
            <div className={styles.title}>{unitStudyHeading}</div>
            <div className={styles.description}>
              <ul className={styles.boxView}>
              {skills.map((skill, index) => {
                return (
                  <li key={index} className={styles.questionBox}>
                    {skill}
                  </li>
                );
              })}
              </ul>
            </div>
          </section>
          {studyGuideMaterials && studyGuideMaterials?.length > 0 && 
          <div className={styles.materialView}>
            <Button size='small' className={styles.materialButton} label='DOWNLOAD LESSON MATERIALS' 
              leftIcon={
                <Tooltip
                  placement='topLeft'
                  title={<div className={styles.sensitiveTooltipText}>
                    {'Download the Lesson Materials'}<br /> {'before beginning the Lesson'}
                  </div>}
                  arrowPointAtCenter
                  color='#FFFFFF'>
                  <IconOutline className={styles.infoIcon} width={19}/>
                </Tooltip>
              }
              rightIcon={<IconArrowUp className={[styles.arrowIcon, selectMaterial ? styles.materialToggleClose : styles.materialToggleOpen].join(' ')} width={13} />} onClick={handleSelectMaterials}/>
          </div>}
          <>
            <div className={styles.statusTipView} style={{display: completeRequire ? 'flex': 'none', right: "-78px" }}>
              <div className={styles.tipTextView}>
                <div className={styles.tipTitle}>{complete ? "Mark Lesson as Incomplete" : "Mark Lesson as Complete"}</div>
              </div>
              <div className={styles.arrowDown}></div>
            </div>
            <div className={styles.statusView}>
              <button className={completedCheckStyle} onClick={markLessonAsComplete} onMouseOver={() => setCompleteRequire(true)} onMouseLeave={() => setCompleteRequire(false)}>
                {(loading || fetching ) ? <LoadingOutlined style={{ fontSize: 24, borderRadius: 15 }} spin /> : complete ?
                  <>
                    <MarkCheckIcon className={completeCheckIconStyle} width={32} height={32}/>
                    <MarkCrossIcon className={inCompleteCheckIconStyle} width={32} height={32}/>
                  </> :
                  <>
                    {
                      completeRequire
                      ? <MarkCheckIcon className={styles.completedCrossIcon} width={32} height={32} />
                      : <NormalCheckIcon className={styles.normalCheckIcon} width={32} height={32} />
                    }
                  </>
                }
              </button>
            </div>
          </>
        </div>
      </Dropdown>

      {studyGuideMaterials && studyGuideMaterials?.length > 0 ? lessonMaterials : null}
    </div>
  );
}

export default StudyGuide;
